const { loadSimplybookWidgetScript } = require('./loadSimplybookWidgetScript');

// Single source of truth for the booking widget host: used to configure SimplybookWidget,
// to preconnect, and to verify postMessage senders (the widget iframe is served from this origin).
const WIDGET_URL = "https://bigaccounts.simplybook.it";
const WIDGET_ORIGIN = new URL(WIDGET_URL).origin;

export class QuizApp {
	/**
	 * Quiz state model
	 * -----------------
	 * currentSlide    {number}  Zero-based index into the rendered .slide elements.
	 * historyStack    {Array}   Stack of previously-visited slide indices (for Prev).
	 * userAnswers     {Object}  Map of question_id → { question, answer_id, answer_text, next }.
	 * totalSlides     {number}  Total .slide elements rendered in the DOM.
	 *
	 * Widget routing
	 * -----------------
	 * When an answer's `next` value is a string starting with "widget" (e.g. "widget2"),
	 * the quiz skips further questions and jumps directly to a result/booking screen.
	 * Multiple widgetN entries exist because different quiz paths lead to distinct
	 * SimplyBook.me service IDs (CSS setup, demo, enterprise, partnership, franchise …)
	 * and some services are localised per language (EN/FR/ES/PT/ZH — both zh-CN and zh-TW).
	 *
	 * Widget lifecycle  (init → open → close → cleanup → re-init)
	 * -----------------
	 * 1. Quiz reaches a result → showResult(widgetKey) renders the result HTML.
	 * 2. widget.action() fires → _openWidgetAfterDelay(serviceId):
	 *      a. Any pending open-widget timer is cancelled.
	 *      b. createServiceIdInWidget(serviceId) is called:
	 *           i.  cleanupWidgetDom() removes previous widget containers/overlays
	 *               and replaces #sb_enterprise_widget with a fresh clone so no
	 *               stale event listeners from the previous SimplybookWidget init remain.
	 *           ii. new SimplybookWidget({...}) attaches to the fresh button.
	 *      c. A 2-second timeout is stored in _widgetOpenTimeout; it clicks the
	 *         trigger button and closes the quiz modal when it fires.
	 * 3. On modal close (hidden.bs.modal) → onModalClosed():
	 *      a. cleanupWidgetDom() removes any leftover widget DOM.
	 *      b. resetQuizFlow() resets all quiz state + cancels _widgetOpenTimeout,
	 *         so a ghost auto-click cannot fire after the modal has closed.
	 *
	 * Modal a11y / focus restoration nodes
	 * --------------------------------------
	 * lastFocusedBeforeModal  {Element|null}  Element focused when modal opened;
	 *                                          restored on modal close.
	 * modalEl                 {Element|null}  #routing-form__popup DOM node.
	 * modalKeydownHandler     {Function|null} Bound keydown handler (Escape + Tab trap).
	 */
	constructor() {
		const _this = this;

		this.quizData = [
			{
				id: 1,
				question: _t("Have you used SimplyBook.me before?"),
				answers: [
					{ answer_id: "a", text: _t("I don’t have a SimplyBook.me account yet"), next: 2 },
					{ answer_id: "b", text: _t("I just signed up for a free trial"), next: 2 },
					{ answer_id: "c", text: _t("I’ve been using SimplyBook.me for a while"), next: 4 },
				],
			},
			{
				id: 2,
				question: _t("Which of the following best describes your business?"),
				answers: [
					{ answer_id: "a", text: _t("We’re an established company"), next: 3 },
					{ answer_id: "b", text: _t("We have a franchise network"), next: 6 },
					{ answer_id: "c", text: _t("We’re a startup"), next: "widget2" },
					{ answer_id: "d", text: _t("We’re a group of freelancers"), next: "widget2" },
					{ answer_id: "e", text: _t("We’re interested in a partnership or affiliate program"), next: "widget5" },
					{ answer_id: "f", text: _t("We’re a marketplace"), next: "widget2" },
					{ answer_id: "g", text: _t("None of the above"), next: "widget3" },
				],
			},
			{
				id: 3,
				question: _t("How many team members do you plan to have on the system?"),
				answers: [
					{ answer_id: "a", text: _t("1–30"), next: 4 },
					{ answer_id: "b", text: _t("31–60"), next: 4 },
					{ answer_id: "c", text: _t("61–250"), next: 7 },
					{ answer_id: "d", text: _t("251 or more"), next: 7 },
				],
			},
			{
				id: 4,
				question: _t("What’s the main purpose of your meeting?"),
				answers: [
					{ answer_id: "a", text: _t("I’m interested in the Enterprise solution"), next: 5 },
					{ answer_id: "b", text: _t("I’d like paid CSS assistance"), next: "widget1" },
					{ answer_id: "c", text: _t("I’d like paid assistance with an account manager"), next: "widget1" },
					{ answer_id: "d", text: _t("I need a demo session"), next: "widget2" },
					{ answer_id: "e", text: _t("I have 1–2 quick setup questions"), next: "widget3" },
				],
			},
			{
				id: 5,
				question: _t("What’s your approximate budget for this project?"),
				answers: [
					{ answer_id: "a", text: _t("More than $6,000 annually"), next: "widget4" },
					{ answer_id: "b", text: _t("More than $12,000 annually"), next: "widget4" },
					{ answer_id: "c", text: _t("More than $20,000 annually"), next: "widget4" },
					{ answer_id: "d", text: _t("Less than $6,000 annually"), next: "widget2" },
				],
			},
			{
				id: 6,
				question: _t("What’s your approximate budget for this project?"),
				answers: [
					{ answer_id: "a", text: _t("More than $6,000 annually"), next: "widget6" },
					{ answer_id: "b", text: _t("More than $12,000 annually"), next: "widget6" },
					{ answer_id: "c", text: _t("More than $20,000 annually"), next: "widget6" },
					{ answer_id: "d", text: _t("Less than $6,000 annually"), next: "widget2" },
				],
			},
			{
				id: 7,
				question: _t("What’s your approximate budget for this project?"),
				answers: [
					{ answer_id: "a", text: _t("More than $6,000 annually"), next: "widget7" },
					{ answer_id: "b", text: _t("More than $12,000 annually"), next: "widget7" },
					{ answer_id: "c", text: _t("More than $20,000 annually"), next: "widget7" },
					{ answer_id: "d", text: _t("Less than $6,000 annually"), next: "widget2" },
				],
			},
		];

		// ── Widget definitions ──────────────────────────────────────────────────
		// Each widgetN corresponds to a distinct booking-service flow on
		// https://bigaccounts.simplybook.it.  The `action` function determines
		// the correct service ID (by language where needed) and delegates to
		// _openWidgetAfterDelay() which handles cleanup + deferred click.

		this.widgets = {
			// widget1: paid CSS / account-manager assistance (Q4-b, Q4-c)
			widget1: {
				title: _t("Setup evaluation with SimplyBook.me, please wait while the widget opens."),
				action: function () {
					const lang = document.documentElement.getAttribute("lang");
					let serviceId;
					switch (true) {
						case lang.startsWith("es"): serviceId = 75; break;
						case lang.startsWith("pt"): serviceId = 76; break;
						case lang.startsWith("zh"): serviceId = 13; break;
						default: serviceId = 3;
					}
					_this._openWidgetAfterDelay(serviceId, 17);
				},
			},

			// widget2: demo call — default low-budget / small-team path
			// (Q2-c,d,f; Q4-d; Q5-d; Q6-d; Q7-d)
			widget2: {
				title: _t("Demo call with SimplyBook.me: please wait while the widget opens."),
				action: function () {
					const lang = document.documentElement.getAttribute("lang");
					let serviceId;
					switch (true) {
						case lang.startsWith("es"): serviceId = 74; break;
						case lang.startsWith("pt"): serviceId = 70; break;
						case lang.startsWith("zh"): serviceId = 103; break;
						default: serviceId = 52;
					}
					_this._openWidgetAfterDelay(serviceId, 14);
				},
			},

			// widget3: live chat + tutorial link — quick support questions (Q2-g; Q4-e)
			// Does NOT use SimplybookWidget; opens the support chat instead.
			widget3: {
				title: _t("Chat with our team or dive into our quick onboarding tutorials below!"),
				content:
					`<div class="btn-bar" style="display:flex;align-items:center;justify-content:center;flex-wrap:wrap;gap:8px;">` +
					`<a href="https://www.youtube.com/watch?v=TdvpW6XiuW8&list=PLM9QxGer1Y2qTVNQp5n3mNFI6tw_iNf3Y" target="_blank" rel="noopener noreferrer" id="sb-tutorials-link" class="btn btn--secondary btn-icon-left fa-youtube">` +
					_t("New user onboarding tutorials") +
					`</a>` +
					`<button type="button" id="sb-chat-link" class="btn btn--alt">` +
					_t("Open Live Chat") +
					`</button>` +
					`</div>`,
				action: function () {
					document.addEventListener(
						"click",
						function (event) {
							if (event.target.closest("#sb-tutorials-link")) {
								_this.closeRoutingFormPopup();
							}
							if (event.target.closest("#sb-chat-link")) {
								const chatTrigger = document.querySelector("#trigger_support_chat, #supportChat .content");
								if (chatTrigger) chatTrigger.click();
								_this.closeRoutingFormPopup();
							}
						}
					);
				},
			},

			// widget4: enterprise inquiry, localised service IDs (Q5-a,b,c)
			widget4: {
				title: _t("Enterprise inquiry with SimplyBook.me, please wait while the widget opens."),
				action: function () {
					const lang = document.documentElement.getAttribute("lang");
					let serviceId;
					switch (true) {
						case lang.startsWith("en"): serviceId = 53; break;
						case lang.startsWith("fr"): serviceId = 53; break;
						case lang.startsWith("es"): serviceId = 54; break;
						case lang.startsWith("pt"): serviceId = 56; break;
						case lang.startsWith("zh"): serviceId = 57; break;
						default: serviceId = 53;
					}
					_this._openWidgetAfterDelay(serviceId, 11);
				},
			},

			// widget5: partnership / affiliate program (Q2-e)
			widget5: {
				title: _t("Partnership and affiliates, please wait while the widget opens."),
				action: function () {
					_this._openWidgetAfterDelay(14, 18);
				},
			},

			// widget6: franchise / category enterprise inquiry, localised (Q6-a,b,c)
			widget6: {
				title: _t("Category Enterprise inquiry with SimplyBook.me, please wait while the widget opens."),
				action: function () {
					const lang = document.documentElement.getAttribute("lang");
					let serviceId;
					switch (true) {
						case lang.startsWith("en"): serviceId = 58; break;
						case lang.startsWith("fr"): serviceId = 58; break;
						case lang.startsWith("es"): serviceId = 59; break;
						case lang.startsWith("pt"): serviceId = 62; break;
						case lang.startsWith("zh"): serviceId = 64; break;
						default: serviceId = 58;
					}
					_this._openWidgetAfterDelay(serviceId, 13);
				},
			},

			// widget7: large-team enterprise inquiry, localised (Q7-a,b,c)
			widget7: {
				title: _t("Category Enterprise inquiry with SimplyBook.me, please wait while the widget opens."),
				action: function () {
					const lang = document.documentElement.getAttribute("lang");
					let serviceId;
					switch (true) {
						case lang.startsWith("en"): serviceId = 66; break;
						case lang.startsWith("fr"): serviceId = 66; break;
						case lang.startsWith("es"): serviceId = 67; break;
						case lang.startsWith("pt"): serviceId = 63; break;
						case lang.startsWith("zh"): serviceId = 65; break;
						default: serviceId = 66;
					}
					_this._openWidgetAfterDelay(serviceId, 14);
				},
			},
		};

		// ── Quiz navigation state ───────────────────────────────────────────────
		this.currentSlide = 0;
		this.userAnswers = {};
		this.totalSlides = 0;
		this.historyStack = [];
		this.hideTimeout = null;

		/**
		 * ID returned by setTimeout() for the deferred widget open click.
		 * Stored here so resetQuizFlow() can cancel it if the modal closes
		 * before the 2-second delay elapses (prevents a ghost auto-click).
		 */
		this._widgetOpenTimeout = null;

		// A11y / focus helpers
		this.lastFocusedBeforeModal = null;
		this.modalEl = null;
		this.modalKeydownHandler = null;

		this.selectAnswer = this.selectAnswer.bind(this);
		this.handleAnswerClick = this.handleAnswerClick.bind(this);
	}

	init() {
		const setup = () => {
			this.setupEventListeners();
			this.setupSpionBird();
			this.initQuiz();
			this.setupModalA11y();
		};
		if (document.readyState === 'loading') {
			document.addEventListener("DOMContentLoaded", setup);
		} else {
			setup();
		}
	}

	setupEventListeners() {
		document.querySelector(".widget_btn")?.addEventListener("click", () => {
			//console.log("click");
			document.querySelector("#sb_enterprise_widget")?.click();
		});

		window.addEventListener("message", (event) => {
			// bookingDone is posted by the widget iframe, so only its origin is trusted;
			// otherwise any page could post it and fake a Google Ads conversion.
			if (event.origin !== WIDGET_ORIGIN) return;

			if (event.data?.event === "bookingDone") {
				gtag("event", "conversion", {
					send_to: "AW-1020314917/xrbDCP2h05YbEKWKw-YD",
				});
			}
		});
	}

	/**
	 * Adds keyboard support + focus trap for the Bootstrap modal.
	 * Bootstrap already handles much of this, but we guarantee Escape
	 * closes the modal and Tab stays within focusable children.
	 */
	setupModalA11y() {
		this.modalEl = document.getElementById("routing-form__popup");
		if (!this.modalEl) return;

		// Capture the element that had focus just before the modal opens
		// so we can restore it when the modal closes.
		document.addEventListener("click", (e) => {
			const willOpenModal = e.target.closest('[data-target="#routing-form__popup"], [href="#routing-form__popup"], .open-routing-form');
			if (willOpenModal) {
				this.lastFocusedBeforeModal = document.activeElement;
			}
		});

		// Bootstrap events (if Bootstrap is present)
		if (typeof $ !== "undefined" && $.fn?.modal) {
			$(this.modalEl).on("shown.bs.modal", () => {
				this.onModalOpened();
			});

			$(this.modalEl).on("hidden.bs.modal", () => {
				this.onModalClosed();

				this.forceResetBird();
			});
		}
	}

	onModalOpened() {
		// Fire-and-forget: start loading the widget script as soon as the quiz
		// opens, so it's ready well before the user reaches a widget-routed answer.
		loadSimplybookWidgetScript().catch(() => {});

		// SimplybookWidget itself (inside widget.js) talks to this separate origin
		// for its config/timeslots data. Warming the connection now means the
		// DNS+TCP+TLS handshake happens while the user is still answering
		// questions, instead of adding to the wait after the last one.
		this.preconnectWidgetDataOrigin();

		// Move focus to the close button (or first interactive element) on open.
		const closeBtn = this.modalEl.querySelector(".modal__close");
		(closeBtn || this.getFocusableElements(this.modalEl)[0])?.focus();

		// Add Escape close + Tab focus-trap while modal is visible.
		this.modalKeydownHandler = (e) => {
			if (e.key === "Escape") {
				e.preventDefault();
				this.closeRoutingFormPopup();
				return;
			}
			if (e.key === "Tab") {
				this.trapFocus(e, this.modalEl);
			}
		};

		this.modalEl.addEventListener("keydown", this.modalKeydownHandler);
	}

	onModalClosed() {
		// Remove the keydown handler added in onModalOpened().
		if (this.modalKeydownHandler) {
			this.modalEl.removeEventListener("keydown", this.modalKeydownHandler);
			this.modalKeydownHandler = null;
		}

		// Restore focus to the element that triggered the modal.
		if (this.lastFocusedBeforeModal && typeof this.lastFocusedBeforeModal.focus === "function") {
			this.lastFocusedBeforeModal.focus();
		}

		// ── Widget + quiz cleanup ───────────────────────────────────────────
		// cleanupWidgetDom() first so stale DOM is gone before state resets.
		// resetQuizFlow() cancels any pending _widgetOpenTimeout and restores
		// all quiz UI to its initial state, ready for the next modal open.
		// this.cleanupWidgetDom();
		this.resetQuizFlow();
	}

	getFocusableElements(root) {
		if (!root) return [];
		const selectors = [
			'a[href]',
			'button:not([disabled])',
			'input:not([disabled])',
			'select:not([disabled])',
			'textarea:not([disabled])',
			'[tabindex]:not([tabindex="-1"])'
		];
		return Array.from(root.querySelectorAll(selectors.join(",")))
			.filter(el => !el.hasAttribute("disabled") && !el.getAttribute("aria-hidden"));
	}

	trapFocus(e, root) {
		const focusables = this.getFocusableElements(root);
		if (!focusables.length) return;

		const first = focusables[0];
		const last = focusables[focusables.length - 1];

		if (e.shiftKey && document.activeElement === first) {
			e.preventDefault();
			last.focus();
		} else if (!e.shiftKey && document.activeElement === last) {
			e.preventDefault();
			first.focus();
		}
	}

	setupSpionBird() {
		this.btn = document.getElementById("prevBtn");
		this.birdVideo = document.getElementById("dance-bird");
		this.birdBox = document.querySelector(".bird-wrapper");
		this.birdEye = document.querySelector(".bird-eye");
		this.eyes = document.querySelectorAll(".eye");

		this.mouse = { x: 0, y: 0, needsUpdate: false, active: false, rafId: null };

		document.addEventListener("mousemove", (e) => this.handleMouseMove(e));
		this.btn?.addEventListener("mouseover", () => this.handleBirdHover());
		this.btn?.addEventListener("mouseout", () => this.handleBirdLeave());
	}

	handleMouseMove(e) {
		this.mouse.x = e.clientX;
		this.mouse.y = e.clientY;
		this.mouse.needsUpdate = true;

		if (!this.mouse.active) {
			this.mouse.active = true;
			this.animateEyesLoop();
		}
	}

	animateEyesLoop() {
		if (this.mouse.needsUpdate) {
			this.updateEyesPosition();
			this.mouse.needsUpdate = false;
		}
		this.mouse.rafId = requestAnimationFrame(() => this.animateEyesLoop());
	}

	updateEyesPosition() {
		if (!this.eyes?.length) return;

		for (const eye of this.eyes) {
			const wrap = eye.closest(".eye-wrap");
			if (!wrap) continue;

			const rect = wrap.getBoundingClientRect();
			const cx = rect.left + rect.width / 2;
			const cy = rect.top + rect.height / 2;

			const dx = this.mouse.x - cx;
			const dy = this.mouse.y - cy;
			const distance = Math.sqrt(dx ** 2 + dy ** 2) || 1;
			const maxMove = rect.width / 7;
			const scale = Math.min(maxMove / distance, 1);

			// Smooth movement via lerp (15% per frame)
			const prev = eye.dataset.pos ? JSON.parse(eye.dataset.pos) : { x: 0, y: 0 };
			const newX = prev.x + (dx * scale - prev.x) * 0.15;
			const newY = prev.y + (dy * scale - prev.y) * 0.15;

			eye.style.transform = `translate(${newX}px, ${newY}px)`;
			eye.dataset.pos = JSON.stringify({ x: newX, y: newY });
		}
	}

	toggleBirdState(showVideo) {
		if (!this.birdVideo || !this.birdEye) return;

		if (showVideo) {
			this.birdEye.classList.add("hide");

			// iOS-safe
			this.birdVideo.muted = true;
			this.birdVideo.setAttribute("playsinline", "");
			this.birdVideo.setAttribute("webkit-playsinline", "");

			const p = this.birdVideo.play();
			if (p && typeof p.catch === "function") {
				p.catch(() => {
				});
			}
		} else {
			this.birdEye.classList.remove("hide");
			this.birdVideo.pause();
		}
	}

	handleBirdHover() {
		//console.log("moveEye");
		clearTimeout(this.hideTimeout);
		this.birdBox?.classList.add("_disagree");
		this.toggleBirdState(true);
	}

	handleBirdLeave() {
		//console.log("handleBirdLeave");
		this.birdBox?.classList.remove("_disagree");
		this.hideTimeout = setTimeout(() => {
			this.toggleBirdState(false); // ховаємо відео, показуємо очі
		}, 2000);
	}

	initQuiz() {
		const container = document.getElementById("slides-container");
		const loader = document.querySelector(".quiz-container--loader");

		if (container) {
			this.quizData.forEach((question, index) => {
				container.appendChild(this.createSlide(question, index));
			});
		}

		this.totalSlides = document.querySelectorAll(".slide").length;
		this.initNavigation();
		this.updateProgress();

		// Remove loader once slides are ready
		loader?.classList.add("fadeOutLeft");
		setTimeout(() => loader?.remove(), 520);

		// Show first slide and move keyboard focus to it
		this.showSlide(0, { moveFocus: true });
	}

	/**
	 * Creates one slide element.
	 * Answers are rendered as <button> elements for correct semantics
	 * and keyboard accessibility (native Enter/Space activation).
	 */
	createSlide(question, index) {
		const slide = document.createElement("div");
		slide.className = `animated slide ${index === 0 ? "fadeInRight active" : "fadeOutLeft"}`;
		slide.setAttribute("role", "group");
		slide.setAttribute("aria-roledescription", "slide");
		slide.setAttribute("aria-hidden", index === 0 ? "false" : "true");

		// A screen-reader-only hint for keyboard navigation
		const srHintId = `quiz-sr-hint-${question.id}`;
		const srHint = `<p id="${srHintId}" class="sr-only">${_t("Use arrow keys to move between answers. Press Enter or Space to select.")}</p>`;

		slide.innerHTML = `
      <h3 class="title title--h3">${question.question}</h3>
      ${srHint}
      <div class="answers" role="list">
        ${question.answers
			.map((answer, idx) => {
				const btnId = `q${question.id}-a${answer.answer_id}`;
				return `
              <div class="answer-option--wrapper" role="listitem">
                <b aria-hidden="true">${answer.answer_id}</b>
                <button
                  type="button"
                  id="${btnId}"
                  class="answer-option"
                  data-question-id="${question.id}"
                  data-question-text="${this.escapeAttr(question.question)}"
                  data-answer-id="${answer.answer_id}"
                  data-answer-text="${this.escapeAttr(answer.text)}"
                  data-next="${answer.next}"
                  aria-describedby="${srHintId}"
                >
                  ${answer.text}
                </button>
              </div>
            `;
			})
			.join("")}
      </div>
    `;

		// Click handler (mouse / touch)
		slide.querySelectorAll(".answer-option").forEach((btn) => {
			btn.addEventListener("click", (e) => this.handleAnswerClick(e));
		});

		// Keyboard navigation inside slide (arrow keys)
		slide.addEventListener("keydown", (e) => this.handleSlideKeydown(e, slide));

		return slide;
	}

	/**
	 * Arrow-key navigation within a slide:
	 * Up/Left moves focus to the previous answer button,
	 * Down/Right to the next, Home/End to first/last.
	 * Enter/Space activate the focused button via native browser behaviour.
	 */
	handleSlideKeydown(e, slideEl) {
		const keys = ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", "Home", "End"];
		if (!keys.includes(e.key)) return;

		const buttons = Array.from(slideEl.querySelectorAll(".answer-option"));
		if (!buttons.length) return;

		const activeEl = document.activeElement;
		const currentIndex = Math.max(0, buttons.indexOf(activeEl));
		let nextIndex = currentIndex;

		switch (e.key) {
			case "ArrowUp":
			case "ArrowLeft":
				e.preventDefault();
				nextIndex = (currentIndex - 1 + buttons.length) % buttons.length;
				break;
			case "ArrowDown":
			case "ArrowRight":
				e.preventDefault();
				nextIndex = (currentIndex + 1) % buttons.length;
				break;
			case "Home":
				e.preventDefault();
				nextIndex = 0;
				break;
			case "End":
				e.preventDefault();
				nextIndex = buttons.length - 1;
				break;
		}

		buttons[nextIndex]?.focus();
	}

	handleAnswerClick(event) {
		const btn = event.currentTarget;
		if (!btn) return;

		const question_id = parseInt(btn.dataset.questionId, 10);
		const question = btn.dataset.questionText || "";
		const answer_id = btn.dataset.answerId || "";
		const answer_text = btn.dataset.answerText || "";
		const nextRaw = btn.dataset.next;
		const next = this.parseNext(nextRaw);

		if (this.birdVideo) {
			this.birdVideo.muted = true;
			this.birdVideo.playsInline = true; // works like property
		}

		this.selectAnswer(question_id, question, answer_id, answer_text, next);
	}

	parseNext(value) {
		// Widget keys stay as strings; numeric slide IDs are converted.
		if (typeof value === "string" && value.startsWith("widget")) return value;
		const n = parseInt(value, 10);
		return Number.isNaN(n) ? value : n;
	}

	selectAnswer(question_id, question, answer_id, answer_text, next) {
		this.historyStack.push(this.currentSlide);
		this.userAnswers[question_id] = { question, answer_id, answer_text, next };

		if (typeof next === "string" && next.startsWith("widget")) {
			this.showResult(next);
			return;
		}

		this.currentSlide = this.quizData.findIndex((q) => q.id === parseInt(next, 10));
		this.showSlide(this.currentSlide, { moveFocus: true });
		this.updateProgress();
	}

	showResult(widgetKey) {
		const container = document.getElementById("slides-container");
		const resultSection = document.getElementById("result-section");

		if (!container || !resultSection) return;

		container.style.display = "none";
		resultSection.style.display = "block";

		const widget = this.widgets[widgetKey];
		let content = "";

		if (widget?.title) {
			content += `<h3 class="title result-section_title">${widget.title}</h3>`;
		}
		if (widget?.content) {
			content += widget.content;
		}

		resultSection.innerHTML = content;

		if (widget?.action) {
			widget.action();
		}

		this.updateProgress(100);
		this.toggleBirdState(true);
		document.getElementById("prevBtn")?.classList.add("hide");

		// Move focus to the first interactive element in the result section.
		setTimeout(() => {
			const focusable = this.getFocusableElements(resultSection)[0];
			focusable?.focus();
		}, 0);
	}

	showSlide(index, { moveFocus = false } = {}) {
		const slides = Array.from(document.querySelectorAll(".slide"));

		slides.forEach((slide, i) => {
			const isActive = i === index;
			slide.classList.toggle("active", isActive);
			slide.classList.toggle("fadeInRight", isActive);
			slide.classList.toggle("fadeOutLeft", !isActive && i < index);
			slide.setAttribute("aria-hidden", isActive ? "false" : "true");
		});

		this.updateButtons();
		this.updateProgress();

		if (moveFocus) {
			const activeSlide = slides[index];
			const firstAnswerBtn = activeSlide?.querySelector(".answer-option");
			setTimeout(() => firstAnswerBtn?.focus(), 0);
		}
	}

	updateProgress(percent) {
		const progressBar = document.querySelector(".progress");
		if (!progressBar) return;

		const value = percent
			? percent
			: ((this.currentSlide + 1) / this.totalSlides) * 100;

		progressBar.style.width = `${value}%`;

		const progressContainer = progressBar.closest(".progress-bar");
		if (progressContainer) {
			progressContainer.setAttribute("role", "progressbar");
			progressContainer.setAttribute("aria-valuemin", "0");
			progressContainer.setAttribute("aria-valuemax", "100");
			progressContainer.setAttribute("aria-valuenow", String(Math.round(value)));
			progressContainer.setAttribute("aria-label", _t("Quiz progress"));
		}
	}

	updateButtons() {
		const prevBtn = document.getElementById("prevBtn");
		if (!prevBtn) return;

		const isFirst = this.currentSlide === 0;
		prevBtn.disabled = isFirst;
		prevBtn.classList.toggle("hidden", isFirst);
	}

	initNavigation() {
		document.getElementById("prevBtn")?.addEventListener("click", () => this.prevSlide());
		document.getElementById("nextBtn")?.addEventListener("click", () => this.nextSlide());
	}

	nextSlide() {
		if (this.currentSlide < this.totalSlides - 1) {
			this.currentSlide++;
			this.showSlide(this.currentSlide, { moveFocus: true });
		}
	}

	prevSlide() {
		if (this.historyStack.length > 0) {
			const previousSlide = this.historyStack.pop();
			this.currentSlide = previousSlide;
			this.showSlide(this.currentSlide, { moveFocus: true });
		}
	}

	closeRoutingFormPopup() {
		if (typeof $ !== "undefined" && $.fn?.modal) {
			$("#routing-form__popup")?.modal("hide");
			return;
		}

		// Fallback when Bootstrap jQuery plugin is unavailable
		const modal = document.getElementById("routing-form__popup");
		if (modal) {
			modal.classList.remove("show");
			modal.setAttribute("aria-hidden", "true");
		}
	}

	// ── Widget lifecycle helpers ──────────────────────────────────────────────

	/**
	 * Shared helper used by widget1/2/4/5/6/7 actions.
	 *
	 * Cancels any outstanding open-widget timer, initialises a fresh
	 * SimplybookWidget for the given service ID, then schedules a 2-second
	 * delayed click on the trigger button + modal close.
	 *
	 * Storing the timeout in _widgetOpenTimeout allows resetQuizFlow() to
	 * cancel it if the user dismisses the modal before the delay elapses.
	 *
	 * @param {number} serviceId  SimplybookWidget service ID to pre-select.
	 */
	async _openWidgetAfterDelay(serviceId, categoryId) {
		// Cancel any leftover timer from a previous (possibly interrupted) run.
		if (this._widgetOpenTimeout) {
			clearTimeout(this._widgetOpenTimeout);
			this._widgetOpenTimeout = null;
		}

		await this.createServiceIdInWidget(serviceId, categoryId);

		// Guard again: createServiceIdInWidget may have bailed out (modal closed
		// or script failed) — don't schedule an auto-click with no widget attached.
		if (!this.modalEl?.classList.contains("in")) return;

		this._widgetOpenTimeout = setTimeout(() => {
			this._widgetOpenTimeout = null;
			document.querySelector("#sb_enterprise_widget")?.click();
			document.getElementsByClassName("result-section_title")[0]?.classList.add("hide");
			this.closeRoutingFormPopup();
		}, 2000);
	}

	/**
	 * Removes all DOM elements injected by SimplybookWidget (popup containers,
	 * overlays) and replaces the #sb_enterprise_widget trigger button with a
	 * structurally identical clone, stripping any event listeners that the
	 * previous widget instance attached to it.
	 *
	 * This makes it safe to call new SimplybookWidget({...}) repeatedly on the
	 * same page without accumulating stale DOM or listener conflicts.
	 *
	 * @param {Object}  [options]
	 * @param {boolean} [options.keepButton=false]  Skip button replacement when true.
	 */
	/**
	 * Adds a <link rel="preconnect"> for https://bigaccounts.simplybook.it,
	 * the origin SimplybookWidget (widget.js) itself fetches its CSS and
	 * config/timeslot data from. Idempotent — safe to call on every modal open.
	 */
	preconnectWidgetDataOrigin() {
		if (document.querySelector('link[rel="preconnect"][href="' + WIDGET_URL + '"]')) return;

		const link = document.createElement("link");
		link.rel = "preconnect";
		link.href = WIDGET_URL;
		link.crossOrigin = "anonymous";
		document.head.appendChild(link);
	}

	cleanupWidgetDom({ keepButton = false } = {}) {
		// Remove all injected widget popup containers (contain the booking iframe).
		document.querySelectorAll(".simplybook-widget-container").forEach((el) => el.remove());

		// Remove all injected overlay backdrop elements.
		document.querySelectorAll(".simplybook-widget-overlay").forEach((el) => el.remove());

		// Replace the trigger button with a fresh clone to purge stale listeners
		// attached by the previous SimplybookWidget init call.
		if (!keepButton) {
			const btn = document.getElementById("sb_enterprise_widget");
			if (btn && btn.parentNode) {
				const fresh = btn.cloneNode(true);
				btn.parentNode.replaceChild(fresh, btn);
			}
		}
	}

	forceResetBird() {
		if (!this.birdVideo) return;

		//console.log("forceResetBird()");

		this.birdVideo.pause();
		this.birdVideo.currentTime = 0;
		this.birdVideo.removeAttribute("src");
		this.birdVideo.load();

		// Reattach source
		const source = document.createElement("source");
		source.src = this.birdVideo.dataset.src || this.birdVideo.querySelector("source")?.src;
		source.type = "video/mp4";
		this.birdVideo.appendChild(source);
	}

	/**
	 * Resets the quiz to its initial state so a subsequent modal open presents
	 * a fresh quiz rather than leftover content from the previous run.
	 *
	 * Restores: slide index, answer/history tracking, UI visibility of the
	 * questions container and result section, progress bar, prev-button state,
	 * and bird animation.
	 *
	 * Also cancels _widgetOpenTimeout to prevent a ghost auto-click if the
	 * modal is closed before the 2-second widget-open delay has elapsed.
	 */
	resetQuizFlow() {
		// Cancel any pending open-widget timer.
		if (this._widgetOpenTimeout) {
			clearTimeout(this._widgetOpenTimeout);
			this._widgetOpenTimeout = null;
		}

		// Reset navigation state.
		this.currentSlide = 0;
		this.userAnswers = {};
		this.historyStack = [];

		// Clear result section content and hide it.
		const resultSection = document.getElementById("result-section");
		if (resultSection) {
			resultSection.innerHTML = "";
			resultSection.style.display = "none";
		}

		// Re-show the questions container (hidden by showResult()).
		const container = document.getElementById("slides-container");
		if (container) container.style.display = "";

		// Remove "hide" class added to result titles by widget actions.
		const resultTitles = document.getElementsByClassName("result-section_title");
		Array.from(resultTitles).forEach((el) => el.classList.remove("hide"));

		// Restore prev button to initial hidden/disabled state.
		const prevBtn = document.getElementById("prevBtn");
		if (prevBtn) {
			prevBtn.classList.remove("hide");
			prevBtn.classList.add("hidden");
			prevBtn.disabled = true;
		}

		// Stop dancing bird, show the eye illustration.
		this.toggleBirdState(false);

		// Restore first slide and reset progress bar to start.
		this.showSlide(0);
		this.updateProgress();
	}

	/**
	 * Escapes special HTML characters in a value before inserting it into
	 * a data-* attribute to prevent attribute injection.
	 */
	escapeAttr(value) {
		return String(value)
			.replace(/&/g, "&amp;")
			.replace(/"/g, "&quot;")
			.replace(/</g, "&lt;")
			.replace(/>/g, "&gt;");
	}

	/**
	 * Initialises a fresh SimplybookWidget instance bound to the
	 * #sb_enterprise_widget trigger button for the given service ID.
	 *
	 * cleanupWidgetDom() is called first to remove any containers/overlays
	 * from a previous init and replace the trigger button with a fresh clone,
	 * ensuring the widget library always starts from a clean slate.
	 *
	 * @param {number} newServiceId  The service ID to pre-select in the widget.
	 */
	async createServiceIdInWidget(newServiceId, categoryId) {
		// Remove stale widget DOM and reset the trigger button before reinitialising.
		this.cleanupWidgetDom();

		try {
			await loadSimplybookWidgetScript();
		} catch {
			return; // widget script failed to load — nothing more we can do here
		}

		// Guard against the modal having been closed while we were waiting
		// (e.g. very fast quiz completion on a slow connection).
		if (!this.modalEl?.classList.contains("in")) return;

		new SimplybookWidget({
			widget_user_class: "enterprise-widget enterprise-widget__white",
			button_custom_id: "sb_enterprise_widget",
			widget_type: "button",
			url: WIDGET_URL,
			theme: "creative",
			theme_settings: {
				sb_base_color: "#06adef",
				timeline_show_end_time: "0",
				timeline_modern_display: "as_slots",
				dark_font_color: "#474747",
				light_font_color: "#ffffff",
				hide_img_mode: "1",
				sb_busy: "#dad2ce",
				sb_available: "#d3e0f1",
			},
			timeline: "modern",
			datepicker: "inline_datepicker",
			is_rtl: false,
			app_config: {
				predefined: { category: categoryId, service: newServiceId },
			},
		});
	}
}
